import * as pc from "playcanvas";
import type { GameClient } from "../net/GameClient";
import { resumeAudioContext, unlockAudioOnGesture } from "./audioContext";

/**
 * GrenadeSounds — all grenade audio (server-authoritative, separate from the
 * gun fire/reload system in WeaponSounds):
 *   • draw     — equipping the grenade (2D, local ears only)
 *   • idleLoop — molotov only: the rag burning while it's held (2D loop,
 *                started on equip, stopped on switch/throw/round end)
 *   • explode  — detonation, a loud world event (3D positional)
 *   • hit      — bounce off a wall/floor mid-flight (3D positional, quieter)
 *
 * Persistent fire zones (molotov / incendiary) get their own looping crackle
 * via `onZoneState`: a positional loop starts when a fire zone appears and a
 * one-shot fadeout plays just before it burns out. The shared AudioListener
 * on the camera (added by Footsteps / WeaponSounds) is the ears.
 */

interface GrenadeSoundProfile {
  /** One-shot equip sound (2D, local). */
  draw?: string;
  /** Looping equip/idle sound while held (2D, local) — molotov rag. */
  idleLoop?: string;
  /** Throw sound — positional, played as the grenade leaves the hand. */
  throw?: string;
  /** Detonation sound — loud, long-range positional. */
  explode?: string;
  /** Bounce/impact sound — quieter, short-range positional. */
  hit?: string;
}

const FLASH_DRAW = "/audio/flash-draw.mp3";
const FLASH_HIT = "/audio/flash-hit.mp3";

const PROFILES: Record<string, GrenadeSoundProfile> = {
  flash: { draw: FLASH_DRAW, explode: "/audio/flash-explode.mp3", hit: FLASH_HIT },
  // HE — same pin-pull as the flash, own detonation + bounce.
  he: { draw: FLASH_DRAW, explode: "/audio/he-explode.mp3", hit: "/audio/he-hit.mp3" },
  // Smoke — flash draw/bounce; the "smoke emit" hiss as it bursts open.
  smoke: { draw: FLASH_DRAW, explode: "/audio/smoke-emit.mp3", hit: FLASH_HIT },
  // Incendiary (CT) — flash draw; its own throw, canister detonate, and the
  // metal bounce off walls before it ignites on the floor.
  incendiary: {
    draw: FLASH_DRAW,
    throw: "/audio/inc-throw.mp3",
    explode: "/audio/inc-detonate.mp3",
    hit: "/audio/inc-bounce.mp3",
  },
  // Molotov (T) — the rag burns in a loop while held (prepare), and the
  // bottle shatters on the ground. No bounce (it breaks on first contact).
  molotov: { idleLoop: "/audio/molotov-idle-loop.wav", explode: "/audio/molotov-detonate.mp3" },
};

// Breakable glass shatter (server-authoritative `glass_break` event).
const GLASS_SHATTER = "/audio/glass-shatter.mp3";

// Persistent fire-zone audio (any molotov / incendiary inferno).
const FIRE_LOOP = "/audio/fire-loop.wav";
const FIRE_FADEOUT = "/audio/fire-fadeout.mp3";
/** Start the fadeout this many seconds before the fire zone expires. */
const FIRE_FADEOUT_LEAD = 1.3;

// All URLs referenced anywhere — loaded up front.
const ALL_URLS = Array.from(
  new Set([
    ...Object.values(PROFILES).flatMap(
      (p) => [p.draw, p.idleLoop, p.throw, p.explode, p.hit].filter(Boolean) as string[],
    ),
    FIRE_LOOP,
    FIRE_FADEOUT,
    GLASS_SHATTER,
  ]),
);

// ── Positional tuning per role ────────────────────────────────────────
interface PosTuning { ref: number; max: number; rolloff: number; volume: number; cleanupMs: number }
const POS_TUNING: Record<"explode" | "hit" | "fadeout" | "throw", PosTuning> = {
  explode: { ref: 6, max: 90, rolloff: 1.1, volume: 0.9, cleanupMs: 4000 },
  hit: { ref: 3, max: 30, rolloff: 1.0, volume: 0.55, cleanupMs: 1500 },
  fadeout: { ref: 4, max: 28, rolloff: 1.0, volume: 0.6, cleanupMs: 2500 },
  throw: { ref: 4, max: 40, rolloff: 1.0, volume: 0.6, cleanupMs: 1500 },
};

// Fire-zone loop (positional, parked at the inferno).
const FIRE_LOOP_REF = 4;
const FIRE_LOOP_MAX = 28;
const FIRE_LOOP_ROLLOFF = 1.0;
const FIRE_LOOP_VOLUME = 0.6;

const LOCAL_DRAW_VOLUME = 0.5;
const MOLOTOV_LOOP_VOLUME = 0.4;

/** Stable, collision-free slot name for a sound URL. */
const slotName = (url: string): string => url.replace(/[^a-z0-9]/gi, "_");

interface FireZoneEntry { entity: pc.Entity; faded: boolean }

export class GrenadeSounds {
  private readonly app: pc.AppBase;
  private readonly client: GameClient;
  private readonly camera: pc.Entity;
  private readonly localEmitter: pc.Entity;
  /** 2D looping emitter for the molotov rag while it's held. */
  private readonly idleLoopEmitter: pc.Entity;
  private idleLoopUrl: string | null = null;
  /** Live fire-zone loops keyed by server zone id. */
  private readonly fireZones = new Map<number, FireZoneEntry>();
  /** Grenade ids already in flight — a newly-seen id means a fresh throw. */
  private readonly seenGrenadeIds = new Set<number>();
  private readonly assets = new Map<string, pc.Asset>();
  private ready = false;
  private readonly unsubs: Array<() => void> = [];

  constructor(app: pc.AppBase, client: GameClient, camera: pc.Entity) {
    this.app = app;
    this.client = client;
    this.camera = camera;

    // Positional audio needs a listener; idempotent if already added.
    if (!camera.audiolistener) camera.addComponent("audiolistener");

    this.localEmitter = new pc.Entity("grenade-snd-local");
    camera.addChild(this.localEmitter);
    this.idleLoopEmitter = new pc.Entity("grenade-snd-idleloop");
    camera.addChild(this.idleLoopEmitter);

    this.loadAssets();
    unlockAudioOnGesture(this.app);

    // Draw / idle-loop — the local player equipped a grenade.
    this.unsubs.push(
      this.client.onWeaponEquip((msg) => {
        if (msg.sessionId !== this.client.sessionId) return;
        this.onLocalEquip(msg.weaponId);
      }),
    );

    // Explode — detonation, loud + long-range positional.
    this.unsubs.push(
      this.client.onGrenadeDetonate((msg) => {
        const url = PROFILES[msg.kind]?.explode;
        if (url) this.playPositional(url, msg.x, msg.y, msg.z, "explode");
      }),
    );

    // Hit — grenade bounced off geometry mid-flight.
    this.unsubs.push(
      this.client.onGrenadeBounce((msg) => {
        const url = PROFILES[msg.kind]?.hit;
        if (url) this.playPositional(url, msg.x, msg.y, msg.z, "hit");
      }),
    );

    // Glass shatter — the breakable pane broke (positional impact sound).
    this.unsubs.push(
      this.client.onGlassBreak((m) => this.playPositional(GLASS_SHATTER, m.x, m.y, m.z, "hit")),
    );

    // Throw — a grenade newly appearing in flight just left someone's hand.
    this.unsubs.push(this.client.onGrenadeState((msg) => this.onGrenadeState(msg)));

    // Fire zones — looping crackle + fadeout, driven by the zone snapshot.
    this.unsubs.push(this.client.onZoneState((msg) => this.onZoneState(msg)));

    // Stop the molotov idle loop if the round ends — or the local player
    // dies — while it's still held (otherwise the rag keeps crackling).
    this.unsubs.push(this.client.onRoundEnd(() => this.stopIdleLoop()));
    this.unsubs.push(
      this.client.onHit((msg) => {
        if (msg.victimSessionId === this.client.sessionId && msg.victimHealthAfter <= 0) this.stopIdleLoop();
      }),
    );
  }

  destroy(): void {
    for (const u of this.unsubs) u();
    this.stopIdleLoop();
    for (const z of this.fireZones.values()) z.entity.destroy();
    this.fireZones.clear();
    this.idleLoopEmitter.destroy();
    this.localEmitter.destroy();
  }

  // ── Local equip (draw / idle loop) ──────────────────────────────────
  private onLocalEquip(weaponId: string): void {
    const prof = PROFILES[weaponId];
    // Switching off a molotov (or to a non-loop grenade / gun) stops the rag.
    if (!prof?.idleLoop) this.stopIdleLoop();
    if (!prof) return;
    if (prof.idleLoop) this.startIdleLoop(prof.idleLoop);
    else if (prof.draw) this.playLocal(prof.draw);
  }

  private startIdleLoop(url: string): void {
    if (!this.ready || this.idleLoopUrl === url) return; // already looping this clip
    this.stopIdleLoop();
    resumeAudioContext(this.app);
    this.idleLoopEmitter.sound?.play(slotName(url));
    this.idleLoopUrl = url;
  }

  private stopIdleLoop(): void {
    if (!this.idleLoopUrl) return;
    this.idleLoopEmitter.sound?.stop();
    this.idleLoopUrl = null;
  }

  // ── Playback ────────────────────────────────────────────────────────
  private playLocal(url: string): void {
    if (!this.ready) return;
    resumeAudioContext(this.app);
    this.localEmitter.sound?.play(slotName(url));
  }

  /** One-shot positional emitter at (x,y,z); self-destroys after the clip. */
  private playPositional(url: string, x: number, y: number, z: number, role: keyof typeof POS_TUNING): void {
    if (!this.ready) return;
    const asset = this.assets.get(url);
    if (!asset) return;
    resumeAudioContext(this.app);
    const t = POS_TUNING[role];

    const e = new pc.Entity(`grenade-snd-${role}`);
    e.addComponent("sound");
    const s = e.sound;
    if (s) {
      s.positional = true;
      s.distanceModel = pc.DISTANCE_INVERSE;
      s.refDistance = t.ref;
      s.maxDistance = t.max;
      s.rollOffFactor = t.rolloff;
      s.addSlot("s", { asset: asset.id, volume: t.volume, loop: false, autoPlay: false, overlap: true });
    }
    this.app.root.addChild(e);
    e.setPosition(x, y, z);
    s?.play("s");
    // Keep the emitter alive for the full clip (some clips — e.g. the 14 s
    // smoke hiss — run far longer than the default cleanup window).
    const clipMs = (this.assetDurationSec(asset) || 0) * 1000;
    window.setTimeout(() => e.destroy(), Math.max(t.cleanupMs, clipMs + 250));
  }

  // ── Throw — fires once per grenade as it enters flight ──────────────
  private onGrenadeState(msg: { grenades: Array<{ id: number; kind: string; x: number; y: number; z: number }> }): void {
    if (!this.ready) return;
    const current = new Set<number>();
    for (const g of msg.grenades) {
      current.add(g.id);
      if (this.seenGrenadeIds.has(g.id)) continue;
      this.seenGrenadeIds.add(g.id);
      const url = PROFILES[g.kind]?.throw;
      if (url) this.playPositional(url, g.x, g.y, g.z, "throw");
    }
    // Forget ids that left flight (detonated); ids are monotonic, never reused.
    for (const id of this.seenGrenadeIds) if (!current.has(id)) this.seenGrenadeIds.delete(id);
  }

  // ── Fire-zone loop + fadeout ────────────────────────────────────────
  private onZoneState(msg: { zones: Array<{ id: number; kind: "smoke" | "fire"; x: number; y: number; z: number; remainingSec: number }> }): void {
    if (!this.ready) return;
    const seen = new Set<number>();
    for (const z of msg.zones) {
      if (z.kind !== "fire") continue;
      seen.add(z.id);
      let entry = this.fireZones.get(z.id);
      if (!entry) {
        entry = this.spawnFireLoop(z.x, z.y, z.z);
        this.fireZones.set(z.id, entry);
      }
      // Near the end of the burn → swap the loop for the one-shot fadeout.
      if (!entry.faded && z.remainingSec <= FIRE_FADEOUT_LEAD) {
        entry.faded = true;
        entry.entity.sound?.stop();
        this.playPositional(FIRE_FADEOUT, z.x, z.y, z.z, "fadeout");
      }
    }
    // Prune zones that vanished (expired or doused by smoke). A smoke-doused
    // fire just cuts out — no fadeout (it was put out, not burnt out).
    for (const [id, entry] of this.fireZones) {
      if (!seen.has(id)) {
        entry.entity.destroy();
        this.fireZones.delete(id);
      }
    }
  }

  private spawnFireLoop(x: number, y: number, z: number): FireZoneEntry {
    const e = new pc.Entity("grenade-snd-fireloop");
    e.addComponent("sound");
    const s = e.sound;
    const asset = this.assets.get(FIRE_LOOP);
    if (s && asset) {
      s.positional = true;
      s.distanceModel = pc.DISTANCE_INVERSE;
      s.refDistance = FIRE_LOOP_REF;
      s.maxDistance = FIRE_LOOP_MAX;
      s.rollOffFactor = FIRE_LOOP_ROLLOFF;
      s.addSlot("loop", { asset: asset.id, volume: FIRE_LOOP_VOLUME, loop: true, autoPlay: false, overlap: false });
    }
    this.app.root.addChild(e);
    e.setPosition(x, y, z);
    s?.play("loop");
    return { entity: e, faded: false };
  }

  // ── Asset loading ───────────────────────────────────────────────────
  private assetDurationSec(asset: pc.Asset): number {
    const snd = asset.resource as { duration?: number } | undefined;
    return snd?.duration ?? 0;
  }

  private loadAssets(): void {
    let remaining = ALL_URLS.length;
    if (remaining === 0) return;
    for (const url of ALL_URLS) {
      this.app.assets.loadFromUrl(url, "audio", (err, asset) => {
        if (err || !asset) console.warn("[GrenadeSounds] failed to load:", url, err);
        else this.assets.set(url, asset);
        if (--remaining === 0) this.onAssetsReady();
      });
    }
  }

  private onAssetsReady(): void {
    // Local 2D emitter — one-shot draw clips.
    this.localEmitter.addComponent("sound");
    const ls = this.localEmitter.sound;
    if (ls) {
      ls.positional = false;
      for (const prof of Object.values(PROFILES)) {
        if (!prof.draw) continue;
        const asset = this.assets.get(prof.draw);
        if (asset) {
          ls.addSlot(slotName(prof.draw), {
            asset: asset.id, volume: LOCAL_DRAW_VOLUME, loop: false, autoPlay: false, overlap: false,
          });
        }
      }
    }

    // Local 2D looping emitter — molotov rag (one looping slot per idle clip).
    this.idleLoopEmitter.addComponent("sound");
    const is = this.idleLoopEmitter.sound;
    if (is) {
      is.positional = false;
      for (const prof of Object.values(PROFILES)) {
        if (!prof.idleLoop) continue;
        const asset = this.assets.get(prof.idleLoop);
        if (asset) {
          is.addSlot(slotName(prof.idleLoop), {
            asset: asset.id, volume: MOLOTOV_LOOP_VOLUME, loop: true, autoPlay: false, overlap: false,
          });
        }
      }
    }

    this.ready = true;
    console.log("[GrenadeSounds] grenade SFX ready:", Object.keys(PROFILES).join(", "));
  }
}
